import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";

type Status =
  | "success"
  | "warning"
  | "danger"
  | "disabled"
  | "ATENCAO"
  | "active"
  | "CRITICO"
  | "inactive"
  | "OK";

export type ProfileDetails = {
  id: string;
  name: string;
  location: string;
  email: string;
  totalKw: number;
  rankedValue: number; // pode ser negativo/positivo/zero
  status: Status;
  statusLabel: string;
  lastUpdate?: string;
  following?: boolean;
};

type ProfileDetailsDialogProps = {
  open: boolean;
  profile: ProfileDetails | null;
  onClose: () => void;
  onEdit: (id: string | number) => void;
  onViewDetails: (id: string) => void;
  onDisableFollow?: (id: string | number) => void;
  showDisableFollow?: boolean;
  showEditAction?: boolean;
  onOpenReport?: (id: string, tipo: ReportType) => void;
  onCopyWinbackLink?: (id: string) => void;
  showReportActions?: boolean;
};

export type ReportType = "completo" | "winback";

const statusColors: Record<
  Status,
  { bg: string; color: string; border?: string }
> = {
  success: { bg: "#EAF7EE", color: "#1E8E3E" },
  active: { bg: "#EAF7EE", color: "#1E8E3E" },
  inactive: { bg: "#F1F3F4", color: "#5F6368" },
  warning: { bg: "#FFF8E1", color: "#B06A00" },
  danger: { bg: "#FCE8E6", color: "#D93025" },
  disabled: { bg: "#F1F3F4", color: "#5F6368" },
  CRITICO: { bg: "#FCE8E6", color: "#D93025" },
  ATENCAO: { bg: "#FFF8E1", color: "#B06A00" },
  OK: { bg: "#EAF7EE", color: "#1E8E3E" },
};

const MetricCard: React.FC<{
  title: string;
  value: React.ReactNode;
  color?: "default" | "green";
}> = ({ title, value, color = "default" }) => {
  const palette =
    color === "green"
      ? { bg: "#EAF7EE", border: "1px solid #CDE7D6" }
      : { bg: "#EEF3FD", border: "1px solid #E0E7FF" };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: palette.bg,
        border: palette.border,
        fontFamily: "Roboto, sans-serif",
        height: "100px",
        width: "140px",
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Paper>
  );
};

export const ProfileDetailsDialog: React.FC<ProfileDetailsDialogProps> = ({
  open,
  profile,
  onClose,
  onEdit,
  onViewDetails,
  onDisableFollow,
  showDisableFollow,
  showEditAction = false,
  onOpenReport,
  onCopyWinbackLink,
  showReportActions = false,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  if (!profile) return null;

  const s = statusColors[profile.status];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          fontFamily: "Roboto, sans-serif",
          borderRadius: fullScreen ? 0 : 2,
        },
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {profile.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {profile.location} • {profile.email}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          {/* eslint-disable-next-line */}
          {/* @ts-ignore */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard title="Total KW" value={profile.totalKw} />
          </Grid>
          {/* eslint-disable-next-line */}
          {/* @ts-ignore */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Ranqueadas"
              value={Math.abs(profile.rankedValue)}
            />
          </Grid>
          {/* eslint-disable-next-line */}
          {/* @ts-ignore */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: s.bg,
                border: `1px solid ${s.bg}`,
                height: "100px",
                width: "140px",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Status
              </Typography>
              <Chip
                label={profile.statusLabel}
                size="small"
                sx={{
                  backgroundColor: s.bg,
                  color: s.color,
                  fontWeight: 700,
                  border: `1px solid ${s.color}22`,
                }}
              />
            </Paper>
          </Grid>
          {/* eslint-disable-next-line */}
          {/* @ts-ignore */}
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard title="Atualizado" value={profile.lastUpdate ?? "-"} />
          </Grid>
        </Grid>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mt: 3 }}
        >
          {showEditAction && (
            <Button
              startIcon={<EditOutlinedIcon />}
              variant="contained"
              onClick={() => {
                onClose();
                onEdit(profile.id);
              }}
              sx={{ textTransform: "none", width: "100%" }}
            >
              Editar Perfil
            </Button>
          )}

          <Button
            startIcon={<VisibilityOutlinedIcon />}
            variant="outlined"
            onClick={() => onViewDetails(profile.id)}
            sx={{ textTransform: "none", width: "100%" }}
          >
            Ver Detalhes
          </Button>

          {showDisableFollow && (
            <Button
              color="error"
              variant="outlined"
              onClick={() => onDisableFollow!(profile.id)}
              sx={{ textTransform: "none", width: "100%" }}
            >
              Desativar Acompanhamento
            </Button>
          )}
        </Stack>

        {showReportActions && onOpenReport && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: "#F7F9FC",
              border: "1px dashed #E2E6EA",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1, fontWeight: 700, letterSpacing: 0.4 }}
            >
              NOVO RELATÓRIO (PRÉVIA · SÓ ADMIN)
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                startIcon={<AssessmentOutlinedIcon />}
                variant="outlined"
                size="small"
                onClick={() => onOpenReport(profile.id, "completo")}
                sx={{ textTransform: "none", width: "100%" }}
              >
                Relatório completo
              </Button>
              <Button
                startIcon={<HistoryOutlinedIcon />}
                variant="outlined"
                size="small"
                onClick={() => onOpenReport(profile.id, "winback")}
                sx={{ textTransform: "none", width: "100%" }}
              >
                Relatório winback
              </Button>
            </Stack>
            {onCopyWinbackLink && (
              <Button
                startIcon={<LinkOutlinedIcon />}
                variant="text"
                size="small"
                onClick={() => onCopyWinbackLink(profile.id)}
                sx={{ textTransform: "none", mt: 1 }}
              >
                Copiar link de winback (24h)
              </Button>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
